/**
 * ErrorBoundary — React class error boundary with plain-language fallback.
 *
 * Uses toPlainError() to convert any caught render error into a calm,
 * user-facing message. Never shows stack traces or technical jargon.
 *
 * Wire at (or near) the app root so uncaught render errors don't blank
 * the whole page. Inner error boundaries per feature area are also
 * encouraged — see usage notes in error-message-style-guide.md.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 *   // Custom fallback title:
 *   <ErrorBoundary context="Dashboard">
 *     <Dashboard />
 *   </ErrorBoundary>
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { toPlainError } from "@/lib/plain-errors";

/* ──────────────────────────────────────────────────────────────────
 * Types
 * ────────────────────────────────────────────────────────────────── */

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional human-readable area name shown in the fallback title.
   * E.g. "Dashboard", "CRM".  Defaults to "this area".
   */
  context?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  plainTitle: string;
  plainBody: string;
  plainNextAction: string | undefined;
}

/* ──────────────────────────────────────────────────────────────────
 * Component
 * ────────────────────────────────────────────────────────────────── */

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      plainTitle: "Something went wrong",
      plainBody: "Your work is saved. Try reloading to continue.",
      plainNextAction: "Reload page",
    };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const plain = toPlainError(error);
    return {
      hasError: true,
      plainTitle: plain.title,
      plainBody: plain.body,
      plainNextAction: plain.nextAction ?? "Try again",
    };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log for observability without leaking to the user.
    // In production this would forward to an error tracking service.
    console.error("[ErrorBoundary] Caught render error:", error, info);
  }

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      plainTitle: "Something went wrong",
      plainBody: "Your work is saved. Try reloading to continue.",
      plainNextAction: "Reload page",
    });
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const context = this.props.context ?? "this area";
    const { plainTitle, plainBody, plainNextAction } = this.state;

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="flex min-h-[200px] w-full flex-col items-center justify-center gap-4 px-6 py-10 text-center"
      >
        {/* Icon — neutral, not red/alarming */}
        <span
          aria-hidden="true"
          className="flex size-12 items-center justify-center rounded-full bg-[var(--color-canvas-deep)] text-[22px]"
        >
          ⚠
        </span>

        <div className="flex max-w-sm flex-col gap-2">
          <h2 className="text-[15px] font-semibold text-[var(--color-text-strong)]">
            {plainTitle}
          </h2>
          <p className="text-[13px] leading-relaxed text-[var(--color-text-muted)]">
            {plainBody}
          </p>
          <p className="text-[12px] text-[var(--color-text-subtle)]">
            Problem in: {context}
          </p>
        </div>

        <button
          type="button"
          onClick={this.handleReset}
          className="mt-1 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-window-face)] px-4 py-1.5 text-[13px] font-medium text-[var(--color-text-strong)] hover:bg-[var(--color-canvas)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-amber)] transition-colors"
        >
          {plainNextAction}
        </button>
      </div>
    );
  }
}
